// @file Utility for managing viz-specific wish arrangement preferences in localStorage
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { getCustomPropIdFromWishGroupBy, isGroupByCustomProp } from '@@/bits/surface_posts_grouping_helper'
import { WallVizEnum, WallWishGroupByTypes } from '@@/enums'
import type { WallViz, WallWishGroupBy, WishArrangement } from '@@/types'

const STORAGE_KEY = 'padlet_wish_arrangements'

interface VizWishArrangementMapping {
  [wallHashid: string]: {
    arrangement: WishArrangement
    lastUsed: number
  }
}

/**
 * Class for managing viz-specific wish arrangement preferences in storage.
 *
 * This class provides a clean API for storing and retrieving wish arrangement settings
 * for different wall layouts with automatic cleanup.
 */
class WishArrangementPreserver {
  /**
   * Viz types that do not support wish arrangement storage.
   */
  private readonly VIZ_TYPES_WITHOUT_GROUP_BY_OPTIONS: WallViz[] = [WallVizEnum.Wall, WallVizEnum.Canvas]

  /**
   * Checks if a viz type has group by options.
   *
   * @param {WallViz} viz - The visualization type to check
   * @returns {boolean} True if the viz type supports arrangement storage
   */
  private isVizTypeWithGroupBy(viz: WallViz): boolean {
    return !this.VIZ_TYPES_WITHOUT_GROUP_BY_OPTIONS.includes(viz)
  }

  /**
   * Safely retrieves and parses viz wish arrangement data in storage.
   *
   * @returns {VizWishArrangementMapping} Parsed arrangement data or empty object on error
   */
  private getStoredData(): VizWishArrangementMapping {
    try {
      const stored = safeLocalStorage.getItem(STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse viz wish arrangement storage data:', error)
      return {}
    }
  }

  /**
   * Safely stores viz wish arrangement data to storage.
   *
   * @param {VizWishArrangementMapping} data - The arrangement data to store
   */
  private setStoredData(data: VizWishArrangementMapping): void {
    try {
      safeLocalStorage.setItem(STORAGE_KEY, asciiSafeStringify(data))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to store viz wish arrangement data:', error)
    }
  }

  /**
   * Removes entries that are older than 30 days.
   */
  private cleanupOldEntries(): void {
    const data = this.getStoredData()
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
    let hasChanges = false

    const cleanedData: VizWishArrangementMapping = {}

    Object.entries(data).forEach(([wallHashid, entry]) => {
      if (entry != null && typeof entry === 'object' && entry.lastUsed >= thirtyDaysAgo) {
        // Keep recent entries
        cleanedData[wallHashid] = entry
      } else {
        // Mark for removal
        hasChanges = true
      }
    })

    // Only update storage if we made changes
    if (hasChanges) {
      this.setStoredData(cleanedData)
    }
  }

  /**
   * Cleans up invalid settings for a specific wall.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   */
  private cleanUpInvalidSettings(wallHashid: string): void {
    const data = this.getStoredData()
    const { [wallHashid]: _, ...updatedData } = data
    this.setStoredData(updatedData)
  }

  /**
   * Checks if the settings are valid.
   *
   * @param {WallWishGroupBy | null} groupBy - The group_by value to validate
   * @param {boolean} showAuthor - Whether the author is visible
   * @param {Record<string, any>} customProperties - The custom properties
   * @returns {boolean} True if the settings are valid
   */
  private isSettingsValid(
    groupBy: WallWishGroupBy | null,
    { showAuthor, customProperties }: { showAuthor: boolean; customProperties: Record<string, any> },
  ): boolean {
    if (groupBy == null) return true
    if (groupBy === WallWishGroupByTypes.Author && !showAuthor) return false
    if (isGroupByCustomProp(groupBy)) {
      const customPropertyId = getCustomPropIdFromWishGroupBy(groupBy)
      if (!customPropertyId) return false
      return customProperties[customPropertyId] != null
    }
    return true
  }

  /**
   * Checks if wish arrangement should be saved for a specific wall.
   *
   * @param {WallViz} currentViz - The current wall visualization type
   * @param {WallViz} nextViz - The next wall visualization type
   * @returns {boolean} True if wish arrangement should be saved
   */
  shouldSaveWishArrangement(currentViz: WallViz, nextViz: WallViz): boolean {
    if (!currentViz || !nextViz) return false
    if (!this.isVizTypeWithGroupBy(currentViz) || this.isVizTypeWithGroupBy(nextViz)) return false
    return true
  }

  /**
   * Saves wish arrangement settings for a specific wall.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WishArrangement} arrangement - The wish arrangement to save
   */
  saveSettingsForViz(wallHashid: string, arrangement: WishArrangement): void {
    if (!wallHashid || arrangement == null) return

    this.cleanupOldEntries()
    const data = this.getStoredData()

    data[wallHashid] = {
      arrangement,
      lastUsed: Date.now(),
    }

    this.setStoredData(data)
  }

  /**
   * Checks if wish arrangement should be restored for a specific wall.
   *
   * @param {WallViz} currentViz - The current wall visualization type
   * @param {WallViz} nextViz - The next wall visualization type
   * @returns {boolean} True if wish arrangement should be restored
   */
  shouldRestoreWishArrangement(
    currentViz: WallViz,
    nextViz: WallViz,
    wallHashid: string,
    showAuthor: boolean,
    customProperties: Record<string, any>,
  ): boolean {
    if (!currentViz || !nextViz || !wallHashid) return false
    if (this.isVizTypeWithGroupBy(currentViz) || !this.isVizTypeWithGroupBy(nextViz)) return false
    const data = this.getStoredData()
    const wallData = data[wallHashid]
    if (wallData?.arrangement == null) return false

    const arrangement = wallData.arrangement
    if (!this.isSettingsValid(arrangement.group_by, { showAuthor, customProperties })) {
      this.cleanUpInvalidSettings(wallHashid)
      return false
    }

    return true
  }

  /**
   * Retrieves saved wish arrangement settings for a specific wall.
   * Only returns settings if current viz type does not have group by options and next viz type does.
   *
   * @param {string} wallHashid - The unique identifier for the wall
   * @param {WallViz} currentViz - The current wall visualization type
   * @param {WallViz} nextViz - The next wall visualization type
   * @returns {WishArrangement|null} The saved arrangement, or null if not found or viz is unsupported
   */
  getSettingsForViz(wallHashid: string): WishArrangement | null {
    if (!wallHashid) return null

    // Whenever we are getting settings, we should also clean up old entries so users don't have to manually implement
    this.cleanupOldEntries()
    const data = this.getStoredData()
    const wallData = data[wallHashid]
    if (wallData?.arrangement == null) return null

    const arrangement = wallData.arrangement

    // Clean up after successful restore
    const { [wallHashid]: _, ...updatedData } = data
    this.setStoredData(updatedData)
    return arrangement
  }
}

export { WishArrangementPreserver }
